import { Pipe, PipeTransform } from '@angular/core';

/**
 * Masks sensitive values so only the last 4 characters show.
 * Usage: {{ row | mask:json }}           -> json.name === 'tag_data': masks key_value_display of every
 *                                           row.tag_data item with masking_required = true, returns row
 *        {{ mobile | mask }}             -> XXXXXX3210
 *        {{ aadhaar | mask:6 }}          -> XXXXXX789012 (custom visible count)
 *        {{ card | mask:4:'X':true }}    -> XXXX-XXXX-XXXX-1234 (keeps separators)
 *        {{ (identity | mask).key_value_display }} -> returns the identity item with key_value_display
 *                                           masked when masking_required is true, else the item as-is
 */
@Pipe({
    name: 'mask',
    standalone: false
})
export class MaskPipe implements PipeTransform {

    transform(data: any, json: any = 4, maskChar: string = 'X', keepSeparators: boolean = false): any {
        if (data === null || data === undefined || data === '') {
            return data;
        }
        // Grid column config: mask identities inside row.tag_data
        if (json && typeof json === 'object') {
            if (json.name && json.name.toLowerCase() === 'tag_data') {
                if (data[json.name] && data[json.name].length > 0) {
                    data[json.name].forEach((item: any) => {
                        if (item.masking_required && item.key_value_display) {
                            item.key_value_display = this.maskingIdentity(item.key_value_display,maskChar);
                        }
                    });
                }
            }
            return data;
        }
        const visible = typeof json === 'number' ? json : 4;
        // Identity object e.g. { key_value_display, key_value_code, masking_required }:
        // return a copy of the item with key_value_display masked (original left untouched)
        if (typeof data === 'object') {
            if (data.masking_required !== true) {
                return data;
            }
            const display = data.key_value_display ?? data.key_value_code ?? '';
            return { ...data, key_value_display: this.maskValue(display, visible, maskChar, keepSeparators) };
        }
        return this.maskValue(data, visible, maskChar, keepSeparators);
    }

    maskingIdentity(value: any, maskChar: string = 'X'): string {
        if (value && typeof value === 'string') {
            const length = value.length;
            if (length <= 4) {
                return maskChar.repeat(length);
            } else {
                const visibleChars = 4;
                const maskedChars = length - visibleChars;
                return maskChar.repeat(maskedChars) + value.slice(-visibleChars);
            }
        }
        return value;
    }

    private maskValue(value: any, visible: number, maskChar: string, keepSeparators: boolean): string {
        const str = String(value);
        if (!keepSeparators) {
            if (str.length <= visible) {
                return str;
            }
            return maskChar.repeat(str.length - visible) + str.slice(-visible);
        }
        // Mask only alphanumerics, leave separators (space, -, /) in place
        let remaining = visible;
        const chars = str.split('');
        for (let i = chars.length - 1; i >= 0; i--) {
            if (/[a-z0-9]/i.test(chars[i])) {
                if (remaining > 0) {
                    remaining--;
                } else {
                    chars[i] = maskChar;
                }
            }
        }
        return chars.join('');
    }

}
